// biome-ignore lint/style/noRestrictedImports: Link.resolveHref only serializes an href.
import { type Href, Link } from "expo-router";

import { isInternalHref } from "./href";

const PROPAGATED_PARAM_PREFIX = "utm_";

export const PROPAGATED_PARAM_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  // Below are persisted for internal tracking purposes
  "partner",
  "origin", // Internal landing page the user came from, e.g. `walmart-glp-1`
] as const;

type PropagatedParamKey =
  | (typeof PROPAGATED_PARAM_KEYS)[number]
  | `${typeof PROPAGATED_PARAM_PREFIX}${string}`;

export type PropagatedUrlParams = {
  [K in PropagatedParamKey]?: string | string[];
};

/** Tracking params only - other params (e.g. `msg`) can carry private text */
export const isPropagatedParamKey = (key: string): key is PropagatedParamKey =>
  key.startsWith(PROPAGATED_PARAM_PREFIX) ||
  (PROPAGATED_PARAM_KEYS as readonly string[]).includes(key);

/* url.pathname flattens relative urls like "../x" to "/x", this preserves the path */
export const getPathFromHref = (href: string) =>
  href.split("?")[0].split("#")[0];

/** Merge an Expo Router url with propagated URL params */
const mergeUrlWithParams = (
  href: string,
  propagatedUrlParams: PropagatedUrlParams,
) => {
  // Use Expo pattern of "file:" base for parsing as a dummy domain without using a dummy url that may flag scanners
  const url = new URL(href, "file:");
  for (const [key, value] of Object.entries(propagatedUrlParams))
    if (!url.searchParams.has(key))
      for (const single of [value ?? []].flat())
        url.searchParams.append(key, single);

  return `${getPathFromHref(href)}${url.search}${url.hash}`;
};

export const mergePropagatedUrlParams = (
  href: Href,
  propagatedUrlParams: PropagatedUrlParams,
): Href => {
  const resolved = typeof href === "string" ? href : Link.resolveHref(href);

  return isInternalHref(resolved)
    ? (mergeUrlWithParams(resolved, propagatedUrlParams) as Href)
    : href;
};
